import React from 'react';
import { maxWidthContainerWithoutGutters } from '@noths/polaris-client-ribbons-base';
import {
  PartnerPageHeader,
  PressComment,
  TextAndImage,
} from '@noths/polaris-client-ribbons-design-system';

import { Tabs } from 'src/components/molecules/Tabs/Tabs';
import { TextBlock } from 'src/components/molecules/TextBlock/TextBlock';
import type {
  ContentstackPartner,
  ImageConnection,
} from 'src/services/contentstack/types/PartnerPage.types';
import * as styles from './PartnerStorefrontLayout.styles';

const getImages = (imageConnection: ImageConnection) => {
  return imageConnection.edges
    .filter((edge) => {
      return !!edge;
    })
    .map((edge) => ({
      alt: edge.node.description,
      src: edge.node.url,
    }));
};

interface PartnerStorefrontLayoutProps {
  partner: ContentstackPartner;
}

export const PartnerStorefrontLayout = ({ partner }: PartnerStorefrontLayoutProps) => {
  return (
    <>
      <div css={styles.partnerPageHeaderWrapper}>
        <div css={maxWidthContainerWithoutGutters}>
          <PartnerPageHeader
            buttonHref={`/partners/${partner.shortcode}/products`}
            buttonText="Shop our products"
            coverImageSrc={partner.cover_imageConnection.edges[0]?.node.url || ''}
            logoImageSrc={partner.logoConnection.edges[0]?.node.url || ''}
            name={partner.name}
            startDate={partner.partner_start_date}
          />
        </div>
      </div>
      <div css={styles.container}>
        <Tabs
          ariaLabel="Partner products and content navigation"
          tabs={[
            {
              href: `/partners/${partner.shortcode}/products`,
              label: 'Shop',
            },
            {
              active: true,
              href: `/partners/${partner.shortcode}`,
              label: 'Our story',
            },
          ]}
        />
        <br />
        <TextAndImage
          body={partner.story_text || ''}
          heading="About"
          images={getImages(partner.story_imagesConnection)}
          subHeading={partner.story_summary || ''}
          textColumn="left"
        />
        {partner.more_about_you && (
          <TextAndImage
            body={partner.more_about_you.text || ''}
            heading={partner.more_about_you.summary || ''}
            images={getImages(partner.more_about_you.imageConnection)}
            textColumn="right"
          />
        )}
        {partner.press_comments?.length ? (
          <section css={styles.pressCommentsSection}>
            <h2>Press comments</h2>
            <ul>
              {partner.press_comments.map((comment) => (
                <li key={`${comment.author}-${comment.date}`}>
                  <PressComment
                    comment={comment.text}
                    date={comment.date}
                    href={comment.link}
                    source={comment.author}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <hr />
        <TextBlock
          footerLinkHref="https://info.notonthehighstreet.com/hc/en-us/articles/360007444693-Delivery-Information?_gl=1%2A36y4bz%2A_gcl_au%2AMjA2OTgzNTU0MC4xNzY1ODk4Njk3"
          footerLinkText="delivery policy"
          footerText="Please see notonthehighstreet.com's"
          heading="Delivery"
        />
        <hr />
        <TextBlock
          bodyText={partner.returns_address || ''}
          footerLinkHref="https://info.notonthehighstreet.com/hc/en-us/articles/206411168-What-is-your-returns-policy-?_gl=1*36y4bz*_gcl_au*MjA2OTgzNTU0MC4xNzY1ODk4Njk3"
          footerLinkText="returns policy"
          footerText="Please see notonthehighstreet.com's"
          heading="Returns"
        />
        <hr />
        <TextBlock
          footerLinkHref="https://www.notonthehighstreet.com/about/shopper-terms"
          footerLinkText="terms & conditions"
          footerText="Please see notonthehighstreet.com's"
          heading="Terms & conditions"
        />
        {partner.vat_number && (
          <>
            <hr />
            <section css={styles.vatNumberSection}>
              <p>Registered VAT No.: {partner.vat_number}</p>
            </section>
          </>
        )}
      </div>
    </>
  );
};
